import { useParams } from "react-router-dom";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { updateNewReplies } from "../../../../api/PRMAPI";
import { Button, Popover, SegmentedControl, Stack } from "@mantine/core";
import { IconChevronRight, IconStar } from "@tabler/icons-react";
import { useProfileStore } from "../../../../stores/UserStore";
import { deselectAll } from "../utils";
import { transformFilterToServer } from "../../../../functions/utilFunctions";

const defaults = ["opener", "clicker", "answerer", "to_call", "out_of_office"];

const LeadsBulkNewReply = ({ tableSelection, tableFilter, handleClose, handleFinish }) => {
  const lang = useLanguage((s) => s.language);
  const query = useLeadsStore((s) => s.query);

  const globalFilter = useLeadsStore((s) => s.filter);
  const globalSelectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);

  const selectedIds = tableSelection || globalSelectedIds;
  const filter = tableFilter ? transformFilterToServer(tableFilter) : globalFilter;

  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const setReloadAll = useLeadsContainerStore((s) => s.setReloadAll);

  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [isSelection, setIsSelection] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isAll, setIsAll] = useState(false);
  const [newValue, setNewValue] = useState("add");

  const handleSelectFinish = () => {
    // Delete individual IDS
    let selectedIdsColumns = Object.keys(selectedIds);
    let newContainers = [...containers];
    selectedIdsColumns.forEach((c) => {
      if (selectedIds[c]?.length > 0) {
        let i = newContainers.findIndex((x) => x.id.toString() === c.toString());
        if (i > -1) newContainers[i].loading = true;
      }
    });
    selectedColumns.forEach((s) => {
      let i = newContainers.findIndex((x) => x.id.toString() === s.toString());
      if (i > -1) newContainers[i].loading = true;
    });

    deselectAll();
    return setContainers(newContainers);
  };

  const handleSubmit = (type) => {
    setLoading(true);
    setIsSelection(type === 1);
    setIsAll(type === 0);
    setIsFilter(type === "filter");

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (query) {
            v.push({
              field_name: "any_datafield",
              type: "contains",
              value: query,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    let justFilter = {
      mode: "and",
      values: [],
    };
    if (type === "filter" && filter) {
      justFilter.values.push(filter);
    }
    if (type === "filter" && query) {
      justFilter.values.push({
        field_name: "any_datafield",
        type: "contains",
        value: query,
      });
    }

    updateNewReplies({
      user_id: userId
        ? parseInt(userId)
        : useProfileStore.getState().profile.id,
      filter: type === "filter" ? justFilter : parsedFilter,
      contact_ids:
        type === 0 ? [] : Object.values(selectedIds).flatMap((x) => x),
      excluded_contact_ids: type === 0 ? [] : excluded,
      new_reply: newValue === "add" ? true : false,
    })
      .then((response) => {
        if (type === 1) {
          handleSelectFinish();
        } else {
          setReloadAll(true);
        }
        handleClose?.();
        handleFinish?.()
      })
      .finally(() => {
        setLoading(false);
        setIsAll(false);
        setIsSelection(false);
        setIsFilter(false);
      });
  };

  const canExport = () => {
    if (Object.values(selectedIds).flatMap((x) => x).length > 0) {
      return true;
    }
    if (selectedColumns.length > 0) {
      return true;
    }

    return false;
  };

  return (
    <Popover zIndex={300} position="right-start">
      <Popover.Target>
        <Button
          variant="light"
          justify="start"
          leftSection={<IconStar size={16} />}
          rightSection={<IconChevronRight size={16} />}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
        >
          {lang.prm.mark_as_new}
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap={"xs"} w={250}>
          <SegmentedControl
            data={[
              {
                label: lang.targeting_linkedin_google.add,
                value: "add",
              },
              {
                label: lang.components.folder_remove.dialog.submit_button,
                value: "delete",
              },
            ]}
            value={newValue}
            onChange={setNewValue}
          />

          <Button
            loading={isSelection}
            disabled={!canExport() || loading}
            onClick={() => handleSubmit(1)}
            variant="light"
          >
            {lang.contact_lists.export_contacts.selection}
          </Button>

          <Button
            loading={isFilter}
            disabled={
              loading || ((!filter || !filter?.values?.length === 0) && !query)
            }
            onClick={() => handleSubmit("filter")}
            variant="light"
          >
            {lang.contact_lists.export_contacts.filter}
          </Button>

          <Button
            loading={isAll}
            disabled={loading}
            onClick={() => handleSubmit(0)}
            variant="light"
          >
            {lang.contact_lists.tabs_title.all_contacts}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default LeadsBulkNewReply;
